// Chart Components for Dashboard
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { formatNumber, PLATFORMS, getPlatformName, getPlatformBadgeClass } from "./constants";

// Performance Heatmap Component
export const PerformanceHeatmap = ({ data, loading, lang = "en" }) => {
  if (loading) {
    return <div className="skeleton h-[280px] rounded-md" />;
  }

  if (!data || !data.heatmap || data.heatmap.length === 0) {
    return (
      <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">
        {lang === 'ko' ? '데이터가 없습니다' : lang === 'zh' ? '暂无数据' : 'No data available'}
      </div>
    );
  }

  const { heatmap, date_range } = data;
  
  // Color mapping
  const getLevelColor = (level) => {
    switch (level) {
      case 'high': return 'bg-emerald-500';
      case 'medium': return 'bg-amber-400';
      case 'low': return 'bg-red-400';
      default: return 'bg-muted/30';
    }
  };

  const getLevelTooltip = (level, lang) => {
    const labels = {
      high: { en: 'Excellent', ko: '우수', zh: '优秀' },
      medium: { en: 'Average', ko: '보통', zh: '一般' },
      low: { en: 'Below Avg', ko: '저조', zh: '较差' },
      none: { en: 'No posts', ko: '게시물 없음', zh: '无帖子' }
    };
    return labels[level]?.[lang] || labels[level]?.en || '';
  };

  const formatScore = (score, type) => {
    if (type === 'weighted') {
      return score.toLocaleString();
    }
    return `${score.toFixed(2)}%`;
  };

  return (
    <div className="relative">
      {/* Mobile scroll hint */}
      <div className="md:hidden text-xs text-muted-foreground/50 text-right mb-1 flex items-center justify-end gap-1">
        <span>{lang === 'ko' ? '좌우 스크롤' : lang === 'zh' ? '左右滚动' : 'Scroll'}</span>
        <span>&rarr;</span>
      </div>
      <div className="overflow-x-auto overflow-y-visible">
      <div className="min-w-[600px] pt-2">
        {/* Header - Dates */}
        <div className="flex items-center mb-2">
          <div className="w-24 shrink-0" />
          <div className="flex-1 flex">
            {date_range.map((date, idx) => (
              <div 
                key={date} 
                className="flex-1 text-center text-xs text-muted-foreground"
                style={{ minWidth: '32px' }}
              >
                {idx % 2 === 0 ? date.slice(5) : ''}
              </div>
            ))}
          </div>
        </div>

        {/* Heatmap rows */}
        {heatmap.map((row, rowIdx) => (
          <div key={row.platform} className="flex items-center mb-1">
            {/* Platform label */}
            <div className="w-24 shrink-0 flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${getPlatformBadgeClass(row.platform)}`}>
                {getPlatformName(row.platform, lang)}
              </span>
            </div>
            
            {/* Cells */}
            <div className="flex-1 flex gap-0.5">
              {row.cells.map((cell) => (
                <div
                  key={`${row.platform}-${cell.date}`}
                  className="group relative flex-1"
                  style={{ minWidth: '32px' }}
                >
                  <div
                    className={`h-7 rounded-sm ${getLevelColor(cell.level)} transition-all hover:ring-2 hover:ring-primary/50 cursor-pointer`}
                  />
                  
                  {/* Tooltip - show below for top half rows, above for bottom half */}
                  <div className={`absolute left-1/2 -translate-x-1/2 hidden group-hover:block z-[100] ${rowIdx < heatmap.length / 2 ? 'top-full mt-2' : 'bottom-full mb-2'}`}>
                    <div className="bg-popover border border-border rounded-lg p-2 shadow-lg min-w-[140px]">
                      <p className="text-xs font-medium text-foreground">{cell.date}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {getLevelTooltip(cell.level, lang)}
                      </p>
                      {cell.level !== 'none' && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            {lang === 'ko' ? '게시물' : lang === 'zh' ? '帖子' : 'Posts'}: {cell.post_count}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(cell.score_type || row.score_type) === 'weighted' 
                              ? (lang === 'ko' ? '가중치 점수' : lang === 'zh' ? '加权分数' : 'Score')
                              : (lang === 'ko' ? '참여율' : lang === 'zh' ? '参与率' : 'ER')
                            }: {formatScore(cell.score, cell.score_type || row.score_type)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ❤️ {formatNumber(cell.total_likes)} 💬 {formatNumber(cell.total_comments)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mt-4 text-[10px] sm:text-xs text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-sm bg-emerald-500" />
            <span>{lang === 'ko' ? '우수 (상위 25%)' : lang === 'zh' ? '优秀 (前25%)' : 'Excellent (Top 25%)'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-sm bg-amber-400" />
            <span>{lang === 'ko' ? '보통' : lang === 'zh' ? '一般' : 'Average'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-sm bg-red-400" />
            <span>{lang === 'ko' ? '저조 (하위 25%)' : lang === 'zh' ? '较差 (后25%)' : 'Below Avg (Bottom 25%)'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-sm bg-muted/30" />
            <span>{lang === 'ko' ? '게시물 없음' : lang === 'zh' ? '无帖子' : 'No posts'}</span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// Trends Chart Component
export const TrendsChart = ({ data, loading }) => {
  if (loading || !data.length) {
    return <div className="skeleton h-[300px] rounded-md" />;
  }

  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(val) => val.slice(5)} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={formatNumber} />
          <Tooltip
            contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }}
            formatter={(value, name) => [formatNumber(value), name]}
          />
          <Area type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} fill="url(#viewsGradient)" name="Views" />
          <Line type="monotone" dataKey="likes" stroke="#10B981" strokeWidth={2} dot={false} name="Likes" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Platform Distribution Chart - Based on Likes (views not available for all platforms)
export const PlatformChart = ({ data, lang = "en" }) => {
  const chartData = Object.entries(data)
    .map(([platform, stats]) => ({
      name: platform,
      displayName: getPlatformName(platform, lang),
      likes: stats.likes || 0,
      posts: stats.posts || 0,
      color: PLATFORMS.find((p) => p.id === platform)?.color || "#888",
    }))
    .filter((item) => item.likes > 0) // Only show platforms with likes data
    .sort((a, b) => b.likes - a.likes); // Sort by likes descending

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        No engagement data available
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.displayName}</p>
          <p className="text-sm text-muted-foreground">
            <span className="text-emerald-500">{formatNumber(data.likes)}</span> likes
          </p>
          <p className="text-sm text-muted-foreground">
            {data.posts} posts
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie 
          data={chartData} 
          cx="50%" 
          cy="50%" 
          innerRadius={50} 
          outerRadius={80} 
          paddingAngle={2} 
          dataKey="likes"
          nameKey="displayName"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          formatter={(value, entry) => (
            <span style={{ color: entry.color }}>{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Re-export utilities for other components
export { formatNumber, getPlatformBadgeClass } from "./constants";
export { LANGUAGE_COLORS, SENTIMENT_COLORS } from "./constants";
